import { defineNuxtRouteMiddleware, abortNavigation, useError } from "nuxt/app";
import { isBasketRepoError } from "@chatfood/dbe-client/errors";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useBasketStore } from "~/stores/basket";
import type { IDomainOrderingMode } from "~/domain/ordering-mode";
import { OUTLET_STATES } from "~/domain/outlet-state";
import { useBugReporter, usePinia } from "#imports";

const abortWithUnexpectedError = (
  message: string = "Unexpected error trying to get the basket",
) => {
  return abortNavigation({
    message,
    statusCode: 500,
  });
};

export default defineNuxtRouteMiddleware(async (to) => {
  if (useError().value) return;

  const routeOrderingMode = Array.isArray(to.params.orderingMode)
    ? to.params.orderingMode[0]
    : to.params.orderingMode;

  if (!routeOrderingMode) {
    return abortWithUnexpectedError(
      "EnsureBasketIsLoadedMiddleware requires a route with orderingMode parameter.",
    );
  }

  const pinia = usePinia();
  const outlet = useOutletStore(pinia);

  if (!outlet.isLoaded) {
    return abortWithUnexpectedError(
      "EnsureBasketIsLoadedMiddleware outlet must be loaded before.",
    );
  }

  const basket = useBasketStore(pinia);
  const { report } = useBugReporter();

  // A delivery basket without any address strands the user . When a
  // pending location is held in memory, attach it now.
  const repairMissingDeliveryLocation = async () => {
    const shouldRepairMissingDeliveryLocation =
      basket.isLoaded &&
      basket.orderingMode === "delivery" &&
      !basket.addressId &&
      !basket.currentLocation &&
      !!basket.pendingDeliveryLocation;

    if (!shouldRepairMissingDeliveryLocation) return;

    try {
      await basket.applyPendingDeliveryLocation();
    } catch (error) {
      report(error, {
        tags: {
          "code.flag": "middleware-basket",
        },
      });
    }
  };

  if (
    basket.isLoaded &&
    basket.outletId === outlet.id &&
    basket.orderingMode === routeOrderingMode
  ) {
    await repairMissingDeliveryLocation();
    return;
  }

  const business = useBusinessStore(pinia);

  // Applied without marking the basket loaded, so reads stay correct while
  // mutation guards remain active until a real basket exists.
  const basketContext = {
    businessId: business.id,
    outletId: outlet.id,
    // TODO: fix mode to match domain
    orderingMode: routeOrderingMode as IDomainOrderingMode,
    salesChannel: business.salesChannel,
    currency: business.currency,
  };

  if (outlet.isClosed) {
    basket.patch(basketContext);
    return;
  }

  if (outlet.orderingMode.outletState === OUTLET_STATES.PRE_ORDER_ONLY) {
    const hasExistingBasket = basket.summaries.some(
      (s) =>
        s.outletId === outlet.id &&
        s.orderingMode === basketContext.orderingMode &&
        s.salesChannel === business.salesChannel,
    );

    if (!hasExistingBasket) {
      if (basket.isLoaded) {
        const pendingDeliveryLocation = basket.pendingDeliveryLocation;
        basket.reset();
        basket.setPendingDeliveryLocation(pendingDeliveryLocation);
      }

      basket.patch(basketContext);
      return;
    }
  }

  try {
    await basket.loadBasket(
      business.id,
      business.currency,
      outlet.id,
      basketContext.orderingMode,
      business.salesChannel,
    );

    await repairMissingDeliveryLocation();
  } catch (error) {
    if (isBasketRepoError(error) && error.isBusinessRule()) {
      basket.patch(basketContext);
      return;
    }

    report(error, {
      tags: {
        "code.flag": "middleware-basket",
      },
    });

    return abortWithUnexpectedError();
  }
});
